import type React from 'react';
import { ImageOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/utils';
import { COLUMNS } from '@/features/operations-transmitters/components/TransmittersTable';
import type { ColumnDef } from '@/features/operations-transmitters/components/TransmittersTable';
import type { Transmitter } from '@/features/operations-transmitters/types';
import type { RemoteAccessData, ShootbackStatus, TailscaleGateway } from './types';

function getGatewayImageUrl(image: { _id: string; organisation: string }): string {
  return `/api/v4/legacy/v2/files/${image.organisation}/${image._id}`;
}

function handleImgError(e: React.SyntheticEvent<HTMLImageElement>) {
  const el = e.currentTarget;
  el.style.display = 'none';
  const fallback = el.nextElementSibling;
  if (fallback instanceof HTMLElement) fallback.style.display = 'flex';
}

function createImageCol(
  onImageClick: (url: string, name: string) => void,
): ColumnDef<Transmitter> {
  return {
    key: 'image',
    label: 'Image',
    filterType: 'select',
    filterOptions: [
      { label: 'With Image', value: 'yes' },
      { label: 'Without Image', value: 'no' },
    ],
    getValue: (t) => {
      const hasImage = Boolean(t.image) || Boolean(t.image2);
      return hasImage ? 'yes' : 'no';
    },
    render: (t) => {
      const imgs = [t.image, t.image2].filter(Boolean) as { _id: string; organisation: string }[];
      if (imgs.length === 0) return <span className="text-muted-foreground">—</span>;
      return (
        <div className="flex gap-1">
          {imgs.map((img) => {
            const url = getGatewayImageUrl(img);
            return (
              <button
                key={img._id}
                type="button"
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onImageClick(url, t.name || t.dev_id);
                }}
              >
                <img
                  src={url}
                  alt="Gateway"
                  className="h-16 w-16 rounded object-cover border border-border"
                  onError={handleImgError}
                />
                <div
                  className="h-16 w-16 rounded border border-border bg-muted items-center justify-center text-muted-foreground"
                  style={{ display: 'none' }}
                >
                  <ImageOff className="h-5 w-5" />
                </div>
              </button>
            );
          })}
        </div>
      );
    },
  };
}

/** A re-enrolled gateway leaves a stale node with the same MAC: the live one wins. */
function isMoreCurrent(candidate: TailscaleGateway, current: TailscaleGateway): boolean {
  if (candidate.online !== current.online) return candidate.online;
  return new Date(candidate.lastSeen ?? 0).getTime() > new Date(current.lastSeen ?? 0).getTime();
}

function createRemoteAccessCol({ gateways, status }: RemoteAccessData): ColumnDef<Transmitter> {
  // Mesh MACs are uppercase 12-hex; normalize both sides of the join.
  const meshMap = new Map<string, TailscaleGateway>();
  for (const gateway of gateways) {
    if (!gateway.mac) continue;
    const mac = gateway.mac.toUpperCase();
    const current = meshMap.get(mac);
    if (!current || isMoreCurrent(gateway, current)) meshMap.set(mac, gateway);
  }

  return {
    key: 'remoteAccess',
    label: 'Remote Access',
    noFilter: true,
    getValue: (t) => {
      if (status !== 'ready') return 'Unknown';
      const mesh = meshMap.get(t.dev_id?.toUpperCase());
      if (!mesh) return 'Not enrolled';
      return mesh.online ? 'Online' : 'Offline';
    },
    render: (t) => {
      const fw = t.current_firmware_name || t.firmware || '';
      if (fw.startsWith('transmitter_esp32')) {
        return <span className="text-muted-foreground">—</span>;
      }
      if (status === 'loading') {
        return <span className="text-muted-foreground text-xs">Checking…</span>;
      }
      if (status === 'error') {
        return (
          <span className="text-muted-foreground text-xs" title="Could not read the mesh status from Headscale">
            Unknown
          </span>
        );
      }
      const mesh = meshMap.get(t.dev_id?.toUpperCase());
      if (!mesh) {
        return (
          <span className="text-muted-foreground text-xs" title="Not on the ATEK mesh (Tailscale)">
            Not enrolled
          </span>
        );
      }
      if (mesh.online) {
        return (
          <button
            type="button"
            className="flex items-center gap-1.5 rounded px-1 py-0.5 hover:bg-muted cursor-pointer"
            title={`${mesh.tailnetIp} — click to copy`}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              if (mesh.tailnetIp) {
                navigator.clipboard
                  .writeText(mesh.tailnetIp)
                  .then(() => toast.success(`Copied ${mesh.tailnetIp}`))
                  .catch(() => toast.error('Copy failed'));
              }
            }}
          >
            <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden />
            <span className="font-mono text-xs">{mesh.tailnetIp}</span>
          </button>
        );
      }
      return (
        <span className="flex items-center gap-1.5" title={`Last seen: ${mesh.lastSeen ? formatDateTime(mesh.lastSeen) : 'unknown'}`}>
          <span className="h-2 w-2 rounded-full bg-gray-400" aria-hidden />
          <span className="text-muted-foreground text-xs">
            Offline — {mesh.lastSeen ? formatDateTime(mesh.lastSeen) : 'never'}
          </span>
        </span>
      );
    },
    align: 'center',
  };
}

/**
 * @param remoteAccess pass null to hide the column entirely (non-admin
 *   viewers — the backend endpoint is full-admin only)
 */
export function createGatewayColumns(
  shootbackData: ShootbackStatus[],
  remoteAccess: RemoteAccessData | null,
  onToggle: (mac: string, enabled: boolean) => void,
  onImageClick: (url: string, name: string) => void,
): ColumnDef<Transmitter>[] {
  const shootbackMap = new Map(shootbackData.map((s) => [s.mac, s.enabled]));

  const shootbackCol: ColumnDef<Transmitter> = {
    key: 'shootback',
    label: 'Shootback',
    noFilter: true,
    getValue: (t) => String(shootbackMap.get(t.dev_id) ?? false),
    render: (t) => {
      const fw = t.current_firmware_name || t.firmware || '';
      if (fw.startsWith('transmitter_esp32')) {
        return <span className="text-muted-foreground">—</span>;
      }
      const enabled = shootbackMap.get(t.dev_id) ?? false;
      return (
        <Button
          variant={enabled ? 'default' : 'outline'}
          size="sm"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onToggle(t.dev_id, !enabled);
          }}
        >
          {enabled ? 'On' : 'Off'}
        </Button>
      );
    },
    align: 'center',
  };

  // Remove 'sensors' column (not relevant for gateways), add image + remote
  // access + shootback columns. The shootback toggle stays until P6
  // decommission (shootback is still the live remote-access path for the
  // un-migrated fleet); remove its column then.
  const baseColumns = COLUMNS.filter((c) => c.key !== 'sensors');
  return [
    createImageCol(onImageClick),
    ...baseColumns,
    ...(remoteAccess ? [createRemoteAccessCol(remoteAccess)] : []),
    shootbackCol,
  ];
}
